import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import Icon from 'assets/components/icon/Icon';
import { inputStyles } from 'assets/components/inputs/Input';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import CheckWrapper from 'assets/components/inputs/wrappers/CheckWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import { getLocales, Language } from 'assets/locales/Locale';
import LossCodeSection from 'models/enums/LossCodeSection';
import { useLossCodeProvider } from '../LossCode.Hooks';

export default function LossCodeEdit() {
  const { lang } = getLocales();
  const { selected, pageRouter, saveApi, categoryListApi } = useLossCodeProvider();

  const sectionOptions = [
    { id: LossCodeSection.part, title: lang.part },
    { id: LossCodeSection.labour, title: lang.labour },
    { id: LossCodeSection.other, title: lang.otherSublet },
  ];

  const categoryOptions = (language: Locale.Languages) =>
    categoryListApi.payload?.data?.map((it) => ({
      id: it.id,
      title: (language === Language.french ? it.nameFr : it.nameEn) || it.nameEn,
    }));

  return (
    <>
      <ModalForm<Api.Core.LossCode.Save.IRequest>
        subcomponents={ThemeModalFormDefault}
        dependencyArray={[selected, categoryListApi.payload?.data]}
        title={lang.lossCode}
        default={
          selected
            ? {
                ...selected.toJSON({ disableNested: true }),
                lossCodeCategories: selected?.lossCodeCategories?.map(({ id }) => ({ id })) ?? [],
              }
            : { sectionCode: LossCodeSection.part, lossCodeCategories: [] }
        }
        onCancel={pageRouter.updateParams}
        onSubmit={(data) => saveApi.execute(data)}
        validation={(data, errors) => {
          if (!data.code) errors.code = lang.mustNotBeEmpty;
          if (!data.descnEn) errors.descnEn = lang.mustNotBeEmpty;
          if (!data.descFr) errors.descFr = lang.mustNotBeEmpty;
          if (!data.notes) errors.notes = lang.mustNotBeEmpty;
          if (!data.sectionCode) errors.sectionCode = lang.mustNotBeEmpty;
        }}
        tabs={[
          { id: 'info', icon: 'fas-info-circle', text: lang.info },
          { id: 'english', icon: 'fas-language', text: lang.english },
          { id: 'french', icon: 'fas-language', text: lang.french },
        ]}
        render={({ form, activeTab }) =>
          activeTab.id === 'info' ? (
            <>
              <FormWrapper>
                <SelectInput
                  name="sectionCode"
                  label={lang.lossCodeSection}
                  data={sectionOptions}
                  value={form.data.sectionCode}
                  error={form.errors.sectionCode}
                  onChange={form.update}
                />
                <CheckWrapper>
                  <CheckInput
                    name="isInternalOnly"
                    label={lang.internalOnly}
                    class={inputStyles.check.switch}
                    value={form.data.isInternalOnly}
                    onChange={form.update}
                    htmlElementProps={{ title: lang.internalOnlyInfo }}
                  />
                  <CheckInput
                    name="isDisabled"
                    label={lang.disabled}
                    class={inputStyles.check.switch}
                    value={form.data.isDisabled}
                    onChange={form.update}
                    htmlElementProps={{ title: lang.lossCodeDisabledInfo }}
                  />
                </CheckWrapper>
              </FormWrapper>
            </>
          ) : activeTab.id === 'english' ? (
            <FormWrapper>
              <SelectInput
                name="lossCodeCategoryIds"
                label={lang.category}
                data={categoryOptions(Language.english)}
                isMultiselect
                value={form.data.lossCodeCategories?.map((it) => it?.id)}
                onChange={({ lossCodeCategoryIds }) =>
                  form.update({ lossCodeCategories: (lossCodeCategoryIds ?? []).map((id: number) => ({ id })) })
                }
                icon={<Icon class="fas-tags" />}
              />
              <TextInput
                name="code"
                label={lang.code}
                value={form.data.code}
                error={form.errors.code}
                onChange={form.update}
                icon={<Icon class="if-quill-pen" />}
              />
              <TextInput
                name="descnEn"
                label={lang.descnEn}
                value={form.data.descnEn}
                error={form.errors.descnEn}
                onChange={form.update}
                icon={<Icon class="if-quill-pen" />}
              />
              <TextInput
                name="notes"
                label={lang.notes}
                value={form.data.notes}
                error={form.errors.notes}
                onChange={form.update}
                icon={<Icon class="if-quill-pen" />}
              />
            </FormWrapper>
          ) : (
            <FormWrapper>
              <SelectInput
                name="lossCodeCategoryIds"
                label={lang.category}
                data={categoryOptions(Language.french)}
                isMultiselect
                value={form.data.lossCodeCategories?.map((it) => it?.id)}
                onChange={({ lossCodeCategoryIds }) =>
                  form.update({ lossCodeCategories: (lossCodeCategoryIds ?? []).map((id: number) => ({ id })) })
                }
                icon={<Icon class="fas-tags" />}
              />
              <TextInput
                name="code"
                label={lang.code}
                value={form.data.code}
                error={form.errors.code}
                onChange={form.update}
                icon={<Icon class="if-quill-pen" />}
              />
              <TextInput
                name="descFr"
                label={lang.descFr}
                value={form.data.descFr}
                error={form.errors.descFr}
                onChange={form.update}
                icon={<Icon class="if-quill-pen" />}
              />
              <TextInput
                name="notes"
                label={lang.notes}
                value={form.data.notes}
                error={form.errors.notes}
                onChange={form.update}
                icon={<Icon class="if-quill-pen" />}
              />
            </FormWrapper>
          )
        }
      />
    </>
  );
}
